import { useServerClock } from "../../../../../hooks/useServerClock";
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import CustomDatePicker from "../../../../shared/date-picker/Date/CustomDatePicker";
import CustomDateTime from "../../../../shared/date-picker/Time/CustomDateTime";
import { Aviation, Order } from "../../../../../services";
import style from "./SendToMarketplaceModal.module.scss";
import { Calendar, Clock } from "lucide-react";

const SendToMarketplaceModal = ({
  isOpen,
  orderId,
  tenderDateEnd,
  onSuccess,
  onCancel,
  isAvia = false,
}) => {
  const { t } = useTranslation();
  const { isReady: isTimeReady, nowMs } = useServerClock(isOpen && Boolean(tenderDateEnd));
  const initializedDeadlineRef = useRef(null);

  const [tenderDate, setTenderDate] = useState(null);
  const [tenderTime, setTenderTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      initializedDeadlineRef.current = null;
      return;
    }
    if (!isTimeReady || initializedDeadlineRef.current === (tenderDateEnd || "")) return;
    initializedDeadlineRef.current = tenderDateEnd || "";
    if (isOpen) {
      const defaultTime = new Date();
      defaultTime.setHours(18, 0, 0, 0);
      
      if (tenderDateEnd) {
        const providedDate = new Date(tenderDateEnd);
        const now = nowMs;
        
        if (now != null && providedDate > now) {
          setTenderDate(providedDate);
          setTenderTime(providedDate);
          return;
        } else {
          setTenderDate(null);
          setTenderTime(defaultTime);
          return;
        }
      }
      
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setTenderDate(tomorrow);
      setTenderTime(defaultTime);
    }
  }, [isOpen, tenderDateEnd, isTimeReady, nowMs]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!isTimeReady) return;
    if (!tenderDate || !tenderTime) {
      toast.error(t("ordersCreate.validation.tenderEndDateRequired"));
      return;
    }

    try {
      setIsLoading(true);
      
      const combinedDate = new Date(tenderDate);
      combinedDate.setHours(tenderTime.getHours());
      combinedDate.setMinutes(tenderTime.getMinutes());
      combinedDate.setSeconds(0);
      combinedDate.setMilliseconds(0);
      
      const tenderDateEnd = combinedDate.toISOString(); 

      if (isAvia) {
        await Aviation.sendToMarketplace(orderId, tenderDateEnd);
      } else {
        await Order.sendToMarketplace(orderId, tenderDateEnd);
      }
      
      toast.success(t("orderInfo.ordersInfoHeader.sendToMarketplaceSuccess"));
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error sending to marketplace:", error);
      toast.error(t("common.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={style.modalOverlay} data-portal-modal="true">
      <div className={style.modalContainer}>
        <div className={style.modalHeader}>
          <h2 className={style.modalTitle}>
            {t("orderInfo.ordersInfoHeader.sendToMarketplaceTitle")}
          </h2>
          <p className={style.modalSubtitle}>
            {t("orderInfo.ordersInfoHeader.sendToMarketplaceSubtitle")}
          </p>
        </div>

        <div className={style.tenderSection}>
          <h3 className={style.sectionTitle}>
            {t("orderInfo.ordersInfoHeader.tenderDeadline")}
          </h3>

          <div className={style.inputGroup}>
            <div className={style.inputWrapper}>
              <label className={style.inputLabel}>
                {t("orderInfo.ordersInfoHeader.date")}
              </label>
              <div className={style.inputWithIcon}>
                <div className={style.datePickerWrapper}>
                  <CustomDatePicker
                    selected={tenderDate}
                    onChange={(date) => setTenderDate(date)}
                    disabled={isLoading || !isTimeReady}
                    minDate={new Date()}
                    popperPlacement="bottom-start"
                  />
                </div>
                <Calendar className={style.inputIcon} size={20} />
              </div>
            </div>

            <div className={style.inputWrapper}>
              <label className={style.inputLabel}>
                {t("orderInfo.ordersInfoHeader.time")}
              </label>
              <div className={style.inputWithIcon}>
                <div className={style.timePickerWrapper}>
                  <CustomDateTime
                    selected={tenderTime}
                    onChange={(time) => setTenderTime(time)}
                    disabled={isLoading || !isTimeReady}
                  />
                </div>
                <Clock className={style.inputIcon} size={20} />
              </div>
            </div>
          </div>

          <div className={style.infoMessage}>
            <div className={style.infoIcon}>i</div>
            <p className={style.infoText}>
              {t("orderInfo.ordersInfoHeader.tenderDeadlineInfo")}
            </p>
          </div>
        </div>

        <div className={style.modalActions}>
          <CustomButton
            isWhite
            isWhiteBox
            text={t("common.cancel")}
            onClick={onCancel}
            disabled={isLoading}
          />
          <CustomButton
            isDark
            text={t("common.confirm")}
            onClick={handleConfirm}
            disabled={!isTimeReady}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

SendToMarketplaceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  orderId: PropTypes.string.isRequired,
  tenderDateEnd: PropTypes.string,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  isAvia: PropTypes.bool,
};

export default SendToMarketplaceModal;
